import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  Button,
  Pagination,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaExchangeAlt,
  FaSearch,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const getAuthToken = () => localStorage.getItem('authToken');

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const ContraList = () => {
  const [contras, setContras] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 50;
  const navigate = useNavigate();

  useEffect(() => {
    fetchContras(currentPage);
  }, [currentPage]);

  const fetchContras = async (page) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-contras?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setContras(response.data.contras || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching contra transactions:', error);
      setAlert({
        show: true,
        message: 'Error loading contras. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-contra`,
        { contra_id: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message:
          response.data.message || 'Contra transaction deleted successfully!',
        variant: 'success',
      });
      fetchContras(currentPage);
    } catch (error) {
      console.error('Error deleting contra transaction:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error deleting contra transaction. Please try again.',
        variant: 'danger',
      });
    }
  };

  const filteredContras = contras.filter((contra) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      contra.contra_number?.toString().toLowerCase().includes(search) ||
      contra.from_account?.toLowerCase().includes(search) ||
      contra.to_account?.toLowerCase().includes(search) ||
      contra.description?.toLowerCase().includes(search)
    );
  });

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaExchangeAlt style={{ fontSize: '1.8rem' }} />
                Contra Transactions
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                Manage all contra entries
              </p>
            </div>
            <Button
              variant="light"
              onClick={() => navigate('/contra')}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaPlus /> New Contra
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Search and Filter Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <FaSearch
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d',
                fontSize: '1rem',
              }}
            />
            <input
              type="text"
              placeholder="Search by contra no, account, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px 12px 45px',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'all 0.3s ease',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#667eea')}
              onBlur={(e) => (e.target.style.borderColor = '#dee2e6')}
            />
          </div>
        </div>

        {/* Table Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflowX: 'auto',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Loading contras...
              </p>
            </div>
          ) : filteredContras.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6c757d',
              }}
            >
              <FaExchangeAlt
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p style={{ fontSize: '1.1rem', margin: 0 }}>
                {searchTerm
                  ? 'No contras found matching your search'
                  : 'No contra transactions available'}
              </p>
            </div>
          ) : (
            <>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Contra No.
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      From Account
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      To Account
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: '15px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContras.map((contra) => (
                    <tr
                      key={contra.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f8f9ff')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <td
                        style={{
                          padding: '15px',
                          whiteSpace: 'nowrap',
                          color: '#495057',
                        }}
                      >
                        {formatDisplayDate(contra.date)}
                      </td>
                      <td style={{ padding: '15px', whiteSpace: 'nowrap' }}>
                        <Badge
                          bg="secondary"
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            padding: '6px 12px',
                            borderRadius: '6px',
                          }}
                        >
                          #{contra.contra_number}
                        </Badge>
                      </td>
                      <td style={{ padding: '15px', color: '#495057' }}>
                        {contra.from_account}
                      </td>
                      <td style={{ padding: '15px', color: '#495057' }}>
                        {contra.to_account}
                      </td>
                      <td
                        style={{
                          padding: '15px',
                          textAlign: 'right',
                          fontWeight: '600',
                          color: '#6f42c1',
                          fontSize: '1rem',
                        }}
                      >
                        {formatAmount(contra.amount)}
                      </td>
                      <td
                        style={{
                          padding: '15px',
                          color: '#6c757d',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {contra.description || '-'}
                      </td>
                      <td
                        style={{
                          padding: '15px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/edit-contra/${contra.id}`)}
                          style={{
                            borderRadius: '6px',
                            padding: '6px 12px',
                            marginRight: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <FaEdit /> Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete Contra #${contra.contra_number}?`,
                              )
                            ) {
                              handleDelete(contra.id);
                            }
                          }}
                          style={{
                            borderRadius: '6px',
                            padding: '6px 12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <FaTrash /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: '25px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Pagination style={{ margin: 0 }}>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ContraList;
